import type { VectorExportFeature } from '@geosoft/viewer-sdk';
import polygonClipping from 'polygon-clipping';

type Position = [number, number];
type Ring = Position[];
type Polygon = Ring[];
type MultiPolygon = Polygon[];

/**
 * MVT decoders return one clipped geometry per tile. Attribute-table rows need
 * one feature per ID, but simply deduping keeps only the first tile fragment.
 * Union every polygon fragment first so Scale receives the complete polygon
 * and an outer border without tile-cut seams.
 */
export function mergeVectorTileFeatureFragments(
  features: ReadonlyArray<VectorExportFeature>,
): VectorExportFeature[] {
  const groups = new Map<string, VectorExportFeature[]>();
  for (const feature of features) {
    const key = featureIdentity(feature);
    const group = groups.get(key);
    if (group) group.push(feature);
    else groups.set(key, [feature]);
  }

  return [...groups.values()].map((group) => mergeGroup(group));
}

function mergeGroup(group: VectorExportFeature[]): VectorExportFeature {
  const first = group[0];
  if (group.length === 1) return first;
  const polygons = group.flatMap((feature) => polygonsOf(feature.geometry));
  if (polygons.length === 0) return first;

  try {
    const merged = polygonClipping.union(
      polygons[0],
      ...polygons.slice(1),
    ) as MultiPolygon;
    if (merged.length === 0) return first;
    return {
      ...first,
      geometry:
        merged.length === 1
          ? { type: 'Polygon', coordinates: merged[0] }
          : { type: 'MultiPolygon', coordinates: merged },
    };
  } catch (error) {
    console.warn('[vector] failed to merge PMTiles feature fragments', {
      id: first.id,
      fragmentCount: group.length,
      error,
    });
    return {
      ...first,
      geometry: { type: 'MultiPolygon', coordinates: polygons },
    };
  }
}

function polygonsOf(geometry: unknown): MultiPolygon {
  if (!geometry || typeof geometry !== 'object') return [];
  const record = geometry as { type?: unknown; coordinates?: unknown };
  if (record.type === 'Polygon' && isPolygon(record.coordinates)) {
    return [record.coordinates];
  }
  if (
    record.type === 'MultiPolygon'
    && Array.isArray(record.coordinates)
  ) {
    return record.coordinates.filter(isPolygon);
  }
  return [];
}

function isPolygon(value: unknown): value is Polygon {
  return (
    Array.isArray(value)
    && value.length > 0
    && value.every(
      (ring) =>
        Array.isArray(ring)
        && ring.length >= 3
        && ring.every(
          (position) =>
            Array.isArray(position)
            && typeof position[0] === 'number'
            && typeof position[1] === 'number',
        ),
    )
  );
}

function featureIdentity(feature: VectorExportFeature): string {
  if (feature.id != null && feature.id !== '') return `id:${String(feature.id)}`;
  const props = feature.properties ?? {};
  for (const key of ['OBJECTID_1', 'OBJECTID', 'objectid', 'FID', 'fid', 'gid', 'id']) {
    const value = props[key];
    if (value != null && value !== '') return `${key}:${String(value)}`;
  }
  return `row:${JSON.stringify(props)}`;
}
